import React, { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import EmptyCardDealWorkspace from "assets/images/EmptyCardDealWorkspace.png";
import { WorkspaceFolderCloseIcon } from "assets/material-custom-icons";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { useWorkspaceFilter } from "components/Deal-workspace/Hooks/use-workspace-filter";
import { ColumnVisibilityMenu } from "components/Deal-workspace/Menus/ColumnVisibilityMenu";
import { FileActions as DealFileActions, Props as FileActionsProps } from "components/Deal-workspace/Menus/FileActions";
import { FolderActions, Props as FolderActionsProps } from "components/Deal-workspace/Menus/FolderActions";
import { MetadataTable } from "components/Deal-workspace/Workspace-metadata-components/MetadataTable";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { FileViewer } from "components/popups";
import { Menu } from "components/popups";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { EntityType } from "constants/index";
import { useActionHook, useClientParamsHooks, useModals, useNavigate, usePrompt } from "hooks";
import { useDealActions } from "store";
import type { FolderKind, IFile, IFileMetaDetail, IMetaDetails } from "store/types";

import { useClientWorkspaceCtx } from "./context/ClientWorkspace-context";
import { useBulkActionsHook } from "./Hooks/use-bulk-actions-hook";
import { useFileHook } from "./Hooks/use-file-hook";
import { toDealFolderShape, useClientFolderActions } from "./Hooks/use-folder-actions-hook";
import { useFolderHook } from "./Hooks/use-folder-hook";
import { useUpdateCellHook } from "./Hooks/use-update-cell-hook";
import { useWorkspaceFileDrop } from "./Hooks/use-workspace-dnd";
import { BreadCrumbs } from "./Workspace-metadata-components/BreadCrumbs";
import { BulkActionsBar } from "./Workspace-metadata-components/BulkActionsBar";
import { WorkspaceFilter } from "./Workspace-metadata-components/WorkspaceFilter";

/**
 * Reuses the shared `Deal-workspace` `FileActions` UI, but injects the client
 * file hook so download/delete hit the client endpoints. Defined at module
 * scope so its identity is stable across renders.
 */
const FileActions = (props: FileActionsProps) => <DealFileActions {...props} useHook={useFileHook} />;

/** Same idea for folder rows: shared menu, client-scoped create/rename/delete. */
const RowFolderActions = (props: FolderActionsProps) => <FolderActions {...props} useHook={useClientFolderActions} />;

const FileType = {
  PDF: "pdf",
  JPG: "jpg",
  JPEG: "jpeg",
  PNG: "png"
} as const;

const PREVIEW_TYPES: string[] = [FileType.PDF, FileType.JPG, FileType.JPEG, FileType.PNG];

export const WorkspaceMetadata = () => {
  const {
    details,
    detailsLoading,
    handleFetchNextPage,
    setSortBy,
    columnVisibility,
    columnOrder,
    currentFolder,
    folderTitle,
    toggleColumn,
    onFolderClick,
    activeFilters,
    handleChangeFilter,
    selectedItems,
    setSelectedItems,
    refetchFolderMetadata,
    reorderColumns,
    isAggregateView
  } = useClientWorkspaceCtx();

  const { handleBulkDelete, handleBulkDownload } = useBulkActionsHook({
    selectedItems,
    setSelectedItems
  });
  const { noticeModal } = useModals();
  const { isDragOver, handleDragEnter, handleDragOver, handleDragLeave, handleDrop } = useWorkspaceFileDrop();

  const [file, setFile] = useState<IFileMetaDetail | null>(null);

  const navigate = useNavigate();
  const { clientId, sectionId } = useClientParamsHooks();

  // Server-driven workspace filters (same shape/hook as the deal & global workspace).
  const { getGlobalFolderFiltersAction } = useDealActions();
  const { data: workspaceFiltersFromDB = {}, action: fetchWorkspaceFilters } =
    useActionHook(getGlobalFolderFiltersAction);

  useEffect(() => {
    if (clientId) fetchWorkspaceFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const { filtersList, filterTypes } = useWorkspaceFilter({
    currentFolder,
    hideShareWith: true,
    workspaceFiltersFromDB: workspaceFiltersFromDB
      ? {
          dataroom_folder: {},
          regular_folder: workspaceFiltersFromDB.regular_folder
        }
      : {}
  });

  const { onCreateNewFolder, onFileUpload } = useFolderHook({
    folder: {
      id: currentFolder?.id || -1,
      title: currentFolder?.title || "",
      parent_id: currentFolder?.parent_id || null
    },
    refetchHandler: refetchFolderMetadata
  });

  const { handleBulkClassificationUpdate, handleBulkStatusUpdate } = useUpdateCellHook(refetchFolderMetadata);
  const openPrompt = usePrompt();

  const search = useMemo(() => (sectionId ? { section: String(sectionId) } : undefined), [sectionId]);

  const handleRowClick = (detail: IMetaDetails) => {
    if (!detail) return;
    const fileDetail = detail as IFileMetaDetail;

    if (detail.type === EntityType.FOLDER) {
      onFolderClick(detail.id);
      return;
    }

    // Open PDFs in the data-extraction viewer in read mode (same as the global workspace).
    if (detail.type === FileType.PDF) {
      openPrompt({
        componentType: "fullscreen",
        Component: DataExtractionModal,
        props: {
          fileIds: [detail.id],
          fileNames: { [detail.id]: fileDetail.name },
          source: "globalWorkspace" as const,
          initialShowParameters: false,
          onUpdateFile: () => {
            refetchFolderMetadata();
          }
        }
      });
      return;
    }

    if (PREVIEW_TYPES.includes(detail.type)) {
      setFile(fileDetail);
      return;
    }
    navigate({ route: "clientWorkspaceFile", params: { client_id: clientId, file_id: detail.id }, search });
  };

  const selectedFileIds = useMemo(
    () => selectedItems.filter((item) => item.type !== EntityType.FOLDER).map((item) => item.id),
    [selectedItems]
  );

  const handleBulkClassification = (anchorEl: HTMLElement | null) => {
    if (!selectedFileIds.length) {
      noticeModal({ msg: "Please select at least one file." });
      return;
    }
    handleBulkClassificationUpdate(anchorEl, selectedFileIds);
  };

  const handleBulkStatus = (anchorEl: HTMLElement | null) => {
    if (!selectedFileIds.length) {
      noticeModal({ msg: "Please select at least one file." });
      return;
    }
    handleBulkStatusUpdate(anchorEl, selectedFileIds);
  };

  return (
    <Stack direction="column" sx={{ width: "100%", height: "100%", overflow: "hidden" }} gap="16px">
      <Stack direction="column" gap="16px">
        {currentFolder?.id && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <BreadCrumbs showCurrentFolder />
          </Stack>
        )}
        <Stack direction="row" alignItems="center" gap="23px" sx={{ width: "100%" }}>
          <Stack direction="row" alignItems="center" gap="10px" sx={{ flex: 1, minWidth: 0 }}>
            <WorkspaceFolderCloseIcon sx={{ fill: "#0A3142", fontSize: 20 }} />
            <Typography
              noWrap
              sx={{
                color: "#0a3142",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "24px"
              }}
            >
              {folderTitle || ""}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap="8px" sx={{ flexShrink: 0 }}>
            <ColumnVisibilityMenu
              columnVisibility={columnVisibility}
              onToggle={toggleColumn}
              kind={"folder"}
              showDataroomColumns={false}
              hideShareColumn={true}
              showDealColumn={isAggregateView}
            />
            {currentFolder?.id && (
              <FolderActions
                folder={toDealFolderShape(currentFolder)}
                refetchHandler={refetchFolderMetadata}
                useHook={useClientFolderActions}
              />
            )}
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" gap="10px" justifyContent="space-between">
          <WorkspaceFilter
            handleChangeFilter={handleChangeFilter}
            filterTypes={filterTypes}
            filtersList={filtersList}
            applyMode={true}
            state={activeFilters}
            defaultFilterKeys={["document_type", "file_type"]}
          />
          <BulkActionsBar
            selectedItems={selectedItems}
            onClose={() => setSelectedItems([])}
            onDownload={handleBulkDownload}
            onChangeClassification={handleBulkClassification}
            onChangeStatus={handleBulkStatus}
            onDelete={handleBulkDelete}
          />
        </Stack>
      </Stack>
      <Stack
        direction="column"
        sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", position: "relative" }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              border: "2px dashed #5B9BD5",
              borderRadius: "8px",
              backgroundColor: "rgba(200, 220, 240, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none"
            }}
          >
            <Typography sx={{ color: "#0A3142", fontWeight: 600, fontSize: 16 }}>Drop files to upload</Typography>
          </Box>
        )}
        {details.length || detailsLoading || !!Object.keys(activeFilters).length ? (
          <MetadataTable
            kind={"folder" as FolderKind}
            data={details}
            isLoading={detailsLoading}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            onRowClick={handleRowClick}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            reorderColumns={reorderColumns}
            refetchFolderMetadata={refetchFolderMetadata}
            onFetchNextPage={handleFetchNextPage}
            manualSorting
            onSortingChange={setSortBy}
            hideShareColumn
            showDealColumn={isAggregateView}
            FileActionsComponent={FileActions}
            FolderActionsComponent={RowFolderActions}
            useUpdateCellHookOverride={useUpdateCellHook}
          />
        ) : currentFolder ? (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.workspaceFolder.title}
            description={{
              text: EMPTY_CARD_CONTENT.workspaceFolder.description,
              externalText: EMPTY_CARD_CONTENT.workspaceFolder.externalDescription
            }}
            image={{
              src: EmptyCardDealWorkspace,
              sx: { width: 315, height: 140 }
            }}
            actionsElement={
              <Menu
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                icon={
                  <Button
                    sx={{ textTransform: "none", bgcolor: "#071F35" }}
                    startIcon={<AddIcon />}
                    variant="contained"
                  >
                    Add item
                  </Button>
                }
                keepTriggerEnabled
                iconButtonProps={{ sx: { padding: 0, width: "auto" } }}
                options={[
                  {
                    title: "New folder",
                    icon: <WorkspaceFolderCloseIcon sx={{ fill: "#5d7782" }} />,
                    onClick: () => onCreateNewFolder?.(),
                    disabled: !onCreateNewFolder
                  },
                  {
                    title: "Upload file",
                    icon: <FileUploadOutlinedIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
                    onClick: () => onFileUpload?.(),
                    disabled: !onFileUpload
                  }
                ]}
              />
            }
          />
        ) : (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.clientWorkspaceEmpty.title}
            description={{
              text: EMPTY_CARD_CONTENT.clientWorkspaceEmpty.description,
              externalText: EMPTY_CARD_CONTENT.clientWorkspaceEmpty.externalDescription,
              sx: { width: 388 }
            }}
            image={{
              src: EmptyCardDealWorkspace,
              sx: { width: 315, height: 140 }
            }}
            actionsElement={
              <Menu
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                icon={
                  <Button
                    sx={{ textTransform: "none", bgcolor: "#071F35" }}
                    startIcon={<AddIcon />}
                    variant="contained"
                  >
                    Add item
                  </Button>
                }
                keepTriggerEnabled
                iconButtonProps={{ sx: { padding: 0, width: "auto" } }}
                options={[
                  {
                    title: "Upload File",
                    icon: <FileUploadOutlinedIcon />,
                    onClick: onFileUpload,
                    hasLine: false
                  },
                  {
                    title: "New Folder",
                    icon: <CreateNewFolderOutlinedIcon />,
                    onClick: onCreateNewFolder
                  }
                ]}
              />
            }
          />
        )}

        <FileViewer
          file={(file ?? undefined) as Partial<IFile> | undefined}
          isOpen={Boolean(file)}
          onClose={() => setFile(null)}
        />
      </Stack>
    </Stack>
  );
};
